export interface SalaryFAQData {
  avg_annual?: number
  avg_hourly?: number
  keyword_annual?: number
  keyword_salary?: number
  sample_size?: number
  top_paying_states?: { state: string, annual: number, formatted: string }[]
  top_states_text?: string
  location?: string
  keyword?: string
  top_paying_role?: string
  top_paying_state?: string
  top_role?: string
  top_city?: string
  top_state?: string
  pay_frequency?: string
}
export interface FAQItem {
  question: string
  answer: string
}
export type FAQEntry = {
  question: string
  answers: { Answer: string } | Record<string, string>
}
function isValid(value: any): boolean {
  return value !== null && value !== undefined && value !== ''
}
export function generateSalaryFAQQuestions(
  companyName: string,
  data: SalaryFAQData,
  faqType: 'company' | 'role' | 'location' | 'role-location'
): FAQEntry[] {
  if (!data || !isValid(companyName)) return []
  const questions: FAQEntry[] = []
  if (faqType === 'company') {
    if (isValid(data.avg_hourly)) {
      questions.push({
        question: `How much does ${companyName} pay hourly in the U.S.?`,
        answers: {
          Answer: `Salaries vary by role, but entry-level jobs typically earn around <strong>${formatCurrency(data.avg_hourly)}</strong>/hour.`
        }
      })
    }
    if (isValid(data.avg_annual)) {
      questions.push({
        question: `What is the average annual salary at ${companyName}?`,
        answers: {
          Answer: `The average salary across all roles is approximately <strong>${formatCurrency(data.avg_annual)}</strong>/year, based on job listings and public data.`
        }
      })
    }
    if (isValid(data.top_paying_role)) {
      questions.push({
        question: `Which job at ${companyName} pays the most?`,
        answers: {
          Answer: `Roles like <strong>${data.top_paying_role}</strong> typically offer the highest salaries.`
        }
      })
    }
    if (isValid(data.top_paying_state) || isValid(data.top_states_text)) {
      questions.push({
        question: `Do ${companyName} salaries differ by location?`,
        answers: {
          Answer: isValid(data.top_states_text)
            ? `Yes, salaries can vary significantly depending on the state, city, and cost of living. The top paying states are <strong>${data.top_states_text}</strong>.`
            : `Yes, salaries can vary significantly depending on the state, city, and cost of living. <strong>${data.top_paying_state}</strong> is paying the most.`
        }
      })
    }
    if (isValid(data.pay_frequency)) {
      questions.push({
        question: `Does ${companyName} pay weekly or biweekly?`,
        answers: {
          Answer: `Most <strong>${companyName}</strong> employees are paid <strong>${data.pay_frequency}</strong>, though this may vary by region.`
        }
      })
    }
    if (isValid(data.sample_size)) {
      questions.push({
        question: `Where does this salary data come from?`,
        answers: {
          Answer: `From <strong>${formatNumber(data.sample_size)}</strong> job descriptions and public salary databases including the U.S. BLS.`
        }
      })
    }
  } else if (faqType === 'role') {
    if (!isValid(data.keyword)) return questions
    if (
      isValid(data.keyword_salary)
      && isValid(data.keyword_annual)
    ) {
      questions.push({
        question: `How much does a ${data.keyword} at ${companyName} earn in the U.S.?`,
        answers: {
          Answer: `Around <strong>${data.keyword_salary}</strong>/hour or <strong>${data.keyword_annual}</strong>/year, based on our data.`
        }
      })
    }
    questions.push({
      question: `Is the ${data.keyword} salary at ${companyName} competitive?`,
      answers: {
        Answer: `It's in line with similar positions in the industry.`
      }
    })
    if (isValid(data.top_city) && isValid(data.top_state)) {
      questions.push({
        question: `Do ${data.keyword} salaries vary by location?`,
        answers: {
          Answer: `Yes, salaries for ${data.keyword} roles vary by location. In fact, <strong>${data.top_city}, ${data.top_state}</strong> offers the highest average pay.`
        }
      })
    }
    if (isValid(data.top_states_text)) {
      questions.push({
        question: `Where are ${data.keyword} salaries highest at ${companyName}?`,
        answers: {
          Answer: `Salaries for ${data.keyword} roles are typically highest in <strong>${data.top_states_text}</strong>.`
        }
      })
    }
    questions.push({
      question: `Does ${companyName} offer bonuses or raises for ${data.keyword} roles?`,
      answers: {
        Answer: `Bonuses and performance raises are sometimes offered depending on the role and region.`
      }
    })
    if (isValid(data.sample_size)) {
      questions.push({
        question: `Where does this salary data come from?`,
        answers: {
          Answer: `Based on <strong>${data.sample_size}</strong> ${data.keyword} job listings and U.S. Bureau of Labor Statistics (BLS) data.`
        }
      })
    }
  } else if (faqType === 'location') {
    if (!isValid(data.location)) return questions
    if (isValid(data.avg_annual)) {
      questions.push({
        question: `How much does ${companyName} pay in ${data.location}?`,
        answers: {
          Answer: `The average salary is <strong>${formatCurrency(data.avg_annual)}</strong>/year across all roles in ${data.location}.`
        }
      })
    }
    if (isValid(data.top_role)) {
      questions.push({
        question: `Which ${companyName} job pays the most in ${data.location}?`,
        answers: {
          Answer: `Roles like <strong>${data.top_role}</strong> typically earn the highest salaries in ${data.location}.`
        }
      })
    }
    if (isValid(data.pay_frequency)) {
      questions.push({
        question: `What is the pay frequency in ${data.location}?`,
        answers: {
          Answer: `Most <strong>${companyName}</strong> employees in ${data.location} are paid <strong>${data.pay_frequency}</strong>.`
        }
      })
    }
    if (isValid(data.sample_size)) {
      questions.push({
        question: `Where does this salary data come from?`,
        answers: {
          Answer: `Compiled from <strong>${formatNumber(data.sample_size)}</strong> job listings in ${data.location} and data from the U.S. BLS.`
        }
      })
    }
  } else if (faqType === 'role-location') {
    if (!isValid(data.keyword) || !isValid(data.location)) return questions
    if (
      isValid(data.keyword_salary)
      && isValid(data.keyword_annual)
    ) {
      questions.push({
        question: `How much does a ${data.keyword} at ${companyName} earn in ${data.location}?`,
        answers: {
          Answer: `Around <strong>${data.keyword_salary}</strong>/hour or <strong>${formatCurrency(data.keyword_annual)}</strong>/year, based on job listings.`
        }
      })
    }
    if (isValid(data.top_paying_state) || isValid(data.top_states_text)) {
      questions.push({
        question: `Do ${data.keyword} positions at ${companyName} salaries differ by location?`,
        answers: {
          Answer: isValid(data.top_states_text)
            ? `Yes, salaries vary significantly by location due to cost of living differences. The top paying states are <strong>${data.top_states_text}</strong>.`
            : `Yes, salaries can vary significantly depending on the state, city, and cost of living. <strong>${data.top_paying_state}</strong> is paying the most.`
        }
      })
    }
    if (isValid(data.sample_size)) {
      questions.push({
        question: `Where does this salary data come from?`,
        answers: {
          Answer: `Compiled from <strong>${formatNumber(data.sample_size)}</strong> <strong>${data.keyword}</strong> job listings in ${data.location} and data from the U.S. BLS.`
        }
      })
    }
  }
  return questions
}
export function convertFAQEntriesToSchemaItems(
  faqEntries: FAQEntry[]
): FAQItem[] {
  if (!Array.isArray(faqEntries) || faqEntries.length === 0) return []
  return faqEntries
    .map((entry) => {
      let answerText = ''
      if (entry && typeof entry.answers === 'object') {
        if ('Answer' in entry.answers) {
          answerText = String(entry.answers.Answer).replace(
            /<\/?[^>]+(>|$)/g,
            ''
          )
        } else {
          answerText = Object.entries(entry.answers)
            .map(([section, text]) => `${section}: ${text}`)
            .join(' ')
        }
      }
      return {
        question: entry.question || '',
        answer: answerText
      }
    })
    .filter(item => isValid(item.question) && isValid(item.answer))
}
